import { useContext } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import PropTypes from 'prop-types';
import { ReactComponent as BackArrowIcon } from 'assets/iconButtons/back-arrow-icon.svg';
import i18nContext from 'components/i18n-context';
import useNavigationHistory from 'hooks/useNavigationHistory';
import './BackButton.scss';

const BackButton = ({ redirectRoutePath, usePreviousRoute = false }) => {
  const i18n = useContext(i18nContext);
  const location = useLocation();
  const navigate = useNavigate();
  const { getPreviousPath, history } = useNavigationHistory();

  // Check if current page is a redirect target from customer details tabs
  const isRedirectFromCustomerTab = () => {
    const currentPath = location.pathname;
    const redirectPages = ['/email-statistics', '/payment-obligations'];

    // Only check for specific redirect pages (not messages or transactions as they can be accessed directly)
    if (!redirectPages.some((page) => currentPath.startsWith(page))) {
      return false;
    }

    // Check if we have customer ID in search params (indicating redirect from customer page)
    const searchParams = new URLSearchParams(location.search);
    const customerId = searchParams.get('search_text') || searchParams.get('searchText');

    if (!customerId) {
      return false; // Not a redirect from customer page
    }

    // Check if we came from a customer details page
    const previousPath = getPreviousPath();
    if (previousPath && (previousPath.includes('/customers/') || previousPath.includes('/active-customers/'))) {
      return true;
    }

    // Also check the history for customer pages
    const hasCustomerPageInHistory = history.some(
      (path) =>
        (path.includes('/customers/') || path.includes('/active-customers/')) &&
        (path.includes('/corporative/') || path.includes('/individual/'))
    );

    return hasCustomerPageInHistory;
  };

  const navigateBackOneStep = () => {
    if (redirectRoutePath) {
      navigate(redirectRoutePath);
    } else if (usePreviousRoute) {
      const previousPath = getPreviousPath();

      // Special handling for redirect pages with 403 errors
      if (isRedirectFromCustomerTab()) {
        // Extract customer ID from search params
        const searchParams = new URLSearchParams(location.search);
        const customerId = searchParams.get('search_text') || searchParams.get('searchText');

        // Find the customer page in history
        const customerPath = history
          .slice()
          .reverse()
          .find((path) => path.includes('/customers/') || path.includes('/active-customers/'));

        if (customerPath) {
          // Check if the original tab would cause another redirect
          const tabMatch = customerPath.match(/[?&]tab=([^&]+)/);
          const problematicTabs = ['emails', 'payment_obligations', 'paymentObligations'];

          if (tabMatch && problematicTabs.includes(tabMatch[1])) {
            // Replace with safe tab
            const safeCustomerPath = customerPath.replace(/([?&])tab=[^&]+/, '$1tab=customerDetails');
            navigate(safeCustomerPath);
          } else {
            // Navigate to customer page with original tab or query params
            navigate(customerPath);
          }
          return;
        } else if (customerId) {
          // Fallback: construct the path if we have customer ID
          // Try to determine if it's corporate or individual from history
          let customerType = 'corporative'; // default
          const hasIndividual = history.some((path) => path.includes('/individual/'));
          const hasCorporate = history.some((path) => path.includes('/corporative/'));

          if (hasIndividual && !hasCorporate) {
            customerType = 'individual';
          }

          // Determine if active or regular customers
          const isActiveCustomers = history.some((path) => path.includes('/active-customers/'));
          const baseRoute = isActiveCustomers ? '/active-customers' : '/customers';

          // Try to extract the original tab from history
          let originalTab = 'customerDetails'; // default safe tab
          const customerHistoryEntry = history.find((path) => path.includes(`/${customerType}/${customerId}`));
          if (customerHistoryEntry) {
            const tabMatch = customerHistoryEntry.match(/[?&]tab=([^&]+)/);
            if (tabMatch) {
              originalTab = tabMatch[1];
              // Check if this tab would cause redirect
              const problematicTabs = ['emails', 'payment_obligations', 'paymentObligations'];
              if (problematicTabs.includes(originalTab)) {
                originalTab = 'customerDetails'; // Use safe tab instead
              }
            }
          }

          navigate(`${baseRoute}/${customerType}/${customerId}?tab=${originalTab}`);
          return;
        }
      }

      // Default behavior
      navigate(previousPath);
    } else {
      const pathSegments = location.pathname.split('/');

      pathSegments.pop();
      navigate(pathSegments.join('/'));
    }
  };

  return (
    <div
      className={'back-button-container'}
      role={'button'}
      tabIndex={0}
      onClick={navigateBackOneStep}
      onKeyDown={(e) => {
        if (e.key === 'Enter' || e.key === ' ') {
          navigateBackOneStep();
        }
      }}
    >
      <BackArrowIcon />
      <span className={'back-button-text'}>{i18n.getMessage('button.back')}</span>
    </div>
  );
};

BackButton.propTypes = {
  redirectRoutePath: PropTypes.string,
  usePreviousRoute: PropTypes.bool
};

export default BackButton;
